/**
 * 子账号管理组件：展示子账号列表、支持搜索、新增和删除功能。
 * 仅对主账号可见。
 */

import type { ReactElement } from 'react'
import { useState, useMemo, useCallback } from 'react'
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  Loader2,
  Phone,
  Plus,
  Search,
  Trash2,
  User,
  UserCheck,
  UserX,
  Users,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cn } from '@/lib/utils'
import {
  useSubAccounts,
  useCreateSubAccount,
  useDeleteSubAccount,
  useUpdateSubAccountStatus,
} from '@/hooks/useSubAccounts'
import type { SubAccount, SubAccountListParams, SubAccountStatus } from '@/types/subAccount'

/**
 * 脱敏手机号展示
 * @param phone 手机号
 * @returns 脱敏后的手机号
 */
function maskPhone(phone: string): string {
  if (phone.length < 7) {
    return phone
  }
  const prefix: string = phone.slice(0, 3)
  const suffix: string = phone.slice(-4)
  return `${prefix}****${suffix}`
}

/**
 * 格式化日期显示
 * @param dateStr ISO 日期字符串
 * @returns 格式化后的日期
 */
function formatDateTime(dateStr: string | null | undefined): string {
  if (dateStr === null || dateStr === undefined || dateStr === '') {
    return '未登录'
  }
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) {
      return '未登录'
    }
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return '未登录'
  }
}

/**
 * 获取状态标签样式
 * @param status 账号状态
 * @returns Tailwind className
 */
function getStatusClassName(status: SubAccountStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    case 'disabled':
      return 'bg-rose-500/10 text-rose-700 dark:text-rose-400'
  }
}

/**
 * 获取状态显示文本
 * @param status 账号状态
 * @returns 状态文本
 */
function getStatusText(status: SubAccountStatus): string {
  switch (status) {
    case 'active':
      return '正常'
    case 'disabled':
      return '已禁用'
  }
}

/**
 * 表单数据接口
 */
interface FormData {
  username: string
  phone: string
  password: string
}

/**
 * 表单错误接口
 */
interface FormErrors {
  username?: string
  phone?: string
  password?: string
}

/**
 * 子账号管理组件
 * @returns 子账号管理 ReactElement
 */
export function SubAccountManagement(): ReactElement {
  // 搜索关键词
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  // 状态筛选
  const [statusFilter, setStatusFilter] = useState<SubAccountStatus | 'all'>('all')
  // 新增面板状态
  const [isAddSheetOpen, setIsAddSheetOpen] = useState<boolean>(false)
  // 删除确认弹窗
  const [deleteConfirm, setDeleteConfirm] = useState<SubAccount | null>(null)
  // 表单数据
  const [formData, setFormData] = useState<FormData>({
    username: '',
    phone: '',
    password: '',
  })
  // 表单错误
  const [formErrors, setFormErrors] = useState<FormErrors>({})

  // 构建查询参数，避免 exactOptionalPropertyTypes 问题
  const queryParams = useMemo(() => {
    const params: SubAccountListParams = { pageSize: 100 }
    if (searchKeyword.length > 0) {
      params.keyword = searchKeyword
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter
    }
    return params
  }, [searchKeyword, statusFilter])

  // 获取子账号列表
  const {
    data: subAccountsData,
    isLoading,
    isError,
    refetch,
  } = useSubAccounts(queryParams)

  // 创建子账号 mutation
  const createMutation = useCreateSubAccount()
  // 删除子账号 mutation
  const deleteMutation = useDeleteSubAccount()
  // 更新子账号状态 mutation
  const updateStatusMutation = useUpdateSubAccountStatus()

  // 筛选后的子账号列表
  const filteredSubAccounts: SubAccount[] = useMemo((): SubAccount[] => {
    if (subAccountsData?.list === undefined) {
      return []
    }
    return subAccountsData.list
  }, [subAccountsData])

  /**
   * 验证表单
   * @returns 是否通过验证
   */
  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {}

    // 用户名验证
    if (formData.username.trim().length === 0) {
      errors.username = '请输入用户名'
    } else if (formData.username.trim().length < 2) {
      errors.username = '用户名至少 2 个字符'
    } else if (formData.username.trim().length > 20) {
      errors.username = '用户名不能超过 20 个字符'
    }

    // 手机号验证
    const phoneRegex = /^1[3-9]\d{9}$/
    if (formData.phone.trim().length === 0) {
      errors.phone = '请输入手机号'
    } else if (!phoneRegex.test(formData.phone.trim())) {
      errors.phone = '请输入正确的手机号'
    }

    // 密码验证
    if (formData.password.length === 0) {
      errors.password = '请输入初始密码'
    } else if (formData.password.length < 6) {
      errors.password = '密码至少 6 个字符'
    } else if (formData.password.length > 32) {
      errors.password = '密码不能超过 32 个字符'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData])

  /**
   * 重置表单
   */
  const resetForm = useCallback((): void => {
    setFormData({ username: '', phone: '', password: '' })
    setFormErrors({})
  }, [])

  /**
   * 处理创建子账号
   */
  const handleCreate = useCallback((): void => {
    if (!validateForm()) {
      return
    }

    createMutation.mutate(
      {
        username: formData.username.trim(),
        phone: formData.phone.trim(),
        password: formData.password,
      },
      {
        onSuccess: (): void => {
          setIsAddSheetOpen(false)
          resetForm()
        },
        onError: (error: Error): void => {
          setFormErrors({ username: error.message })
        },
      }
    )
  }, [formData, validateForm, createMutation, resetForm])

  /**
   * 处理删除子账号
   */
  const handleDelete = useCallback((): void => {
    if (deleteConfirm === null) {
      return
    }

    deleteMutation.mutate(deleteConfirm.id, {
      onSuccess: (): void => {
        setDeleteConfirm(null)
      },
      onError: (): void => {
        // 错误提示可以通过 toast 等方式展示
        setDeleteConfirm(null)
      },
    })
  }, [deleteConfirm, deleteMutation])

  /**
   * 处理切换子账号状态（禁用/启用）
   * @param account 子账号信息
   */
  const handleToggleStatus = useCallback(
    (account: SubAccount): void => {
      const newStatus: SubAccountStatus = account.status === 'active' ? 'disabled' : 'active'
      updateStatusMutation.mutate({
        id: account.id,
        data: { status: newStatus },
      })
    },
    [updateStatusMutation]
  )

  /**
   * 处理关闭新增面板
   */
  const handleCloseAddSheet = useCallback((): void => {
    setIsAddSheetOpen(false)
    resetForm()
  }, [resetForm])

  /**
   * 处理输入变化
   * @param field 字段名
   * @param value 值
   */
  const handleInputChange = useCallback(
    (field: keyof FormData, value: string): void => {
      setFormData((prev) => ({ ...prev, [field]: value }))
      // 清除对应字段的错误
      if (formErrors[field] !== undefined) {
        setFormErrors((prev) => ({ ...prev, [field]: undefined }))
      }
    },
    [formErrors]
  )

  return (
    <div className="rounded-2xl border bg-card p-6 shadow-sm">
      {/* 标题区域 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">子账号管理</h2>
            <p className="text-sm text-muted-foreground">
              管理公司内的子账号，子账号共享公司算力
            </p>
          </div>
        </div>
        <Button
          type="button"
          onClick={(): void => {
            setIsAddSheetOpen(true)
          }}
        >
          <Plus className="h-4 w-4" />
          添加子账号
        </Button>
      </div>

      {/* 搜索与筛选 */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchKeyword}
            onChange={(event): void => {
              setSearchKeyword(event.target.value)
            }}
            placeholder="搜索用户名或手机号..."
            className={cn(
              'h-10 w-full rounded-lg border border-input bg-background pl-10 pr-3 text-sm outline-none',
              'focus:border-primary focus:ring-1 focus:ring-primary transition-all'
            )}
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'active', 'disabled'] as const).map((status) => {
            const isActive = status === statusFilter
            const label = status === 'all' ? '全部' : status === 'active' ? '正常' : '已禁用'
            return (
              <Button
                key={status}
                type="button"
                size="sm"
                variant={isActive ? 'default' : 'secondary'}
                className={cn(
                  'rounded-full',
                  isActive ? 'shadow-sm' : 'bg-secondary/70 hover:bg-secondary'
                )}
                onClick={(): void => {
                  setStatusFilter(status)
                }}
              >
                {label}
              </Button>
            )
          })}
        </div>
      </div>

      {/* 列表区域 */}
      <div className="mt-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : isError ? (
          <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-destructive" />
            <p className="mt-2 text-sm text-destructive">加载失败，请稍后重试</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={(): void => {
                void refetch()
              }}
            >
              重新加载
            </Button>
          </div>
        ) : filteredSubAccounts.length === 0 ? (
          <div className="rounded-xl border bg-secondary/30 p-8 text-center">
            <UserX className="mx-auto h-10 w-10 text-muted-foreground/50" />
            <p className="mt-3 text-sm text-muted-foreground">
              {searchKeyword.length > 0 || statusFilter !== 'all'
                ? '没有找到匹配的子账号'
                : '暂无子账号，点击上方按钮添加'}
            </p>
            {(searchKeyword.length > 0 || statusFilter !== 'all') && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={(): void => {
                  setSearchKeyword('')
                  setStatusFilter('all')
                }}
              >
                清空筛选
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSubAccounts.map((account) => (
              <div
                key={account.id}
                className="flex flex-col gap-4 rounded-xl border bg-background p-4 transition-colors hover:bg-secondary/30 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex items-center gap-4">
                  {/* 文字头像 */}
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/10">
                    <span className="text-lg font-bold text-primary">
                      {account.username.slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                  {/* 信息 */}
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold tracking-tight">{account.username}</p>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                          getStatusClassName(account.status)
                        )}
                      >
                        {account.status === 'active' ? (
                          <UserCheck className="h-3 w-3" />
                        ) : (
                          <UserX className="h-3 w-3" />
                        )}
                        {getStatusText(account.status)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      {account.phone !== null && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          {maskPhone(account.phone)}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3.5 w-3.5" />
                        最后登录：{formatDateTime(account.lastLoginAt)}
                      </span>
                    </div>
                  </div>
                </div>
                {/* 操作按钮 */}
                <div className="flex items-center gap-2 sm:justify-end">
                  {/* 禁用/启用按钮 */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(
                      account.status === 'active'
                        ? 'text-amber-600 hover:bg-amber-500/10 hover:text-amber-600'
                        : 'text-emerald-600 hover:bg-emerald-500/10 hover:text-emerald-600'
                    )}
                    onClick={(): void => {
                      handleToggleStatus(account)
                    }}
                    disabled={updateStatusMutation.isPending}
                  >
                    {updateStatusMutation.isPending &&
                    updateStatusMutation.variables.id === account.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : account.status === 'active' ? (
                      <Ban className="h-4 w-4" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    {account.status === 'active' ? '禁用' : '启用'}
                  </Button>
                  {/* 删除按钮 */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={(): void => {
                      setDeleteConfirm(account)
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    删除
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 统计信息 */}
      {subAccountsData !== undefined && subAccountsData.total > 0 && (
        <div className="mt-4 flex items-center justify-between border-t pt-4 text-sm text-muted-foreground">
          <span>共 {subAccountsData.total} 个子账号</span>
          <span>
            {filteredSubAccounts.filter((a) => a.status === 'active').length} 个正常 /{' '}
            {filteredSubAccounts.filter((a) => a.status === 'disabled').length} 个已禁用
          </span>
        </div>
      )}

      {/* 新增子账号面板 */}
      <Sheet open={isAddSheetOpen} onOpenChange={setIsAddSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>添加子账号</SheetTitle>
            <SheetDescription>
              为公司添加新的子账号，子账号可以共享公司算力进行生图操作。
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4 px-4">
            {/* 用户名 */}
            <div className="space-y-2">
              <label htmlFor="username" className="text-sm font-medium">
                用户名 <span className="text-destructive">*</span>
              </label>
              <input
                id="username"
                type="text"
                value={formData.username}
                onChange={(event): void => {
                  handleInputChange('username', event.target.value)
                }}
                placeholder="请输入用户名"
                className={cn(
                  'h-10 w-full rounded-lg border bg-background px-3 text-sm outline-none transition-all',
                  formErrors.username !== undefined
                    ? 'border-destructive focus:border-destructive focus:ring-1 focus:ring-destructive'
                    : 'border-input focus:border-primary focus:ring-1 focus:ring-primary'
                )}
              />
              {formErrors.username !== undefined && (
                <p className="text-xs text-destructive">{formErrors.username}</p>
              )}
            </div>

            {/* 手机号 */}
            <div className="space-y-2">
              <label htmlFor="phone" className="text-sm font-medium">
                手机号 <span className="text-destructive">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(event): void => {
                  handleInputChange('phone', event.target.value)
                }}
                placeholder="请输入手机号"
                className={cn(
                  'h-10 w-full rounded-lg border bg-background px-3 text-sm outline-none transition-all',
                  formErrors.phone !== undefined
                    ? 'border-destructive focus:border-destructive focus:ring-1 focus:ring-destructive'
                    : 'border-input focus:border-primary focus:ring-1 focus:ring-primary'
                )}
              />
              {formErrors.phone !== undefined && (
                <p className="text-xs text-destructive">{formErrors.phone}</p>
              )}
            </div>

            {/* 初始密码 */}
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                初始密码 <span className="text-destructive">*</span>
              </label>
              <input
                id="password"
                type="password"
                value={formData.password}
                onChange={(event): void => {
                  handleInputChange('password', event.target.value)
                }}
                placeholder="请输入初始密码"
                className={cn(
                  'h-10 w-full rounded-lg border bg-background px-3 text-sm outline-none transition-all',
                  formErrors.password !== undefined
                    ? 'border-destructive focus:border-destructive focus:ring-1 focus:ring-destructive'
                    : 'border-input focus:border-primary focus:ring-1 focus:ring-primary'
                )}
              />
              {formErrors.password !== undefined && (
                <p className="text-xs text-destructive">{formErrors.password}</p>
              )}
              <p className="text-xs text-muted-foreground">
                密码长度 6-32 位，子账号首次登录后可自行修改密码
              </p>
            </div>
          </div>

          <SheetFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseAddSheet}
              disabled={createMutation.isPending}
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  创建中...
                </>
              ) : (
                '创建子账号'
              )}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* 删除确认弹窗 */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* 遮罩 */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={(): void => {
              if (!deleteMutation.isPending) {
                setDeleteConfirm(null)
              }
            }}
          />
          {/* 弹窗内容 */}
          <div className="relative z-10 w-full max-w-md rounded-2xl border bg-card p-6 shadow-lg">
            <button
              type="button"
              className="absolute right-4 top-4 rounded-lg p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              onClick={(): void => {
                if (!deleteMutation.isPending) {
                  setDeleteConfirm(null)
                }
              }}
              disabled={deleteMutation.isPending}
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <h3 className="mt-4 text-lg font-semibold">确认删除子账号</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                确定要删除子账号「{deleteConfirm.username}」吗？删除后该用户将无法登录系统，此操作不可撤销。
              </p>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1"
                onClick={(): void => {
                  setDeleteConfirm(null)
                }}
                disabled={deleteMutation.isPending}
              >
                取消
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="flex-1"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    删除中...
                  </>
                ) : (
                  '确认删除'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
